import React, { useState, useEffect, useCallback, useRef } from "react";
import { Container, Row, Col, Button, Modal, Form } from "react-bootstrap";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";
import "./blog.css";
import format from "date-fns/format";
import { useAcubeService } from "../../service/acube-api.hook";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/app-context";
import TestimonialCarousel from "../../components/carousels/testimonial-carousel";
import ContactForm from "../../components/forms/contact-form";
import ProductPartners from "../home/sections/product-partners";
import SearchResults from "./search-blog";
import SkeletonScreen from "../../layouts/skeleton";
import axios from "axios";
import SentryTestimonialVideoPlayer from "../../components/videos/sentry-testimonial";
import TestimonialVideoPlayer from "../../components/videos/testimonial-video";

// ---------- Assets ----------
const like =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-unfilled.svg";
const likeFilled =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-filled.svg";
const share =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/share-icon.svg";
const search =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/search-btn.svg";
const copyLink =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/link.svg";
const copySuccess =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/copied.svg";
const closeIcon =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/close-menu.svg";

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, "") + "/";
const APP_DOMAIN = process.env.REACT_APP_DOMAIN;

const tags = [
  "Artificial Intelligence",
  "App Development",
  "Hire Developers",
  "Machine Learning",
  "Digital Marketing",
  "Digital Transformation",
  "Data Engineering",
  "Graphic Design",
  "Blockchain",
  "UIUX",
  "IOT",
  "Web 3.0",
];

// Custom debounce hook with cancel function
const useDebounce = (callback, delay) => {
  const callbackRef = useRef(callback);
  const timeoutRef = useRef();

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFn = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay],
  );

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [debouncedFn, cancel];
};

const BlogComponent = () => {
  // ---------- States ----------
  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const { getProxy, postProxy } = useAcubeService();
  const [blogData, setBlogData] = useState({
    recentBlogs: [],
    mostViewedBlogs: [],
    popularBlogs: [],
  });
  const { ipAddress } = useAppContext();
  const [isSearched, setIsSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const latestSearchRef = useRef(null);
  const isSearchCancelledRef = useRef(false);

  const navigate = useNavigate();

  // ---------- Share ----------
  const shareURL = currentBlog ? `${APP_DOMAIN}/blogs/${currentBlog.slug}` : "";

  const handleShareClick = (blog) => {
    setCurrentBlog(blog);
    setShowModal(true);
    setIsCopied(false);
  };
  const handleCloseModal = () => setShowModal(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareURL);
      setIsCopied(true);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  // ---------- Fetch Blogs ----------
  const fetchBlogs = async () => {
    setIsLoading(true);
    try {
      const result = await getProxy(`blog/limited-blogs/${ipAddress}`);
      setBlogData(result.data || { recentBlogs: [], mostViewedBlogs: [], popularBlogs: [] });
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setBlogData({ recentBlogs: [], mostViewedBlogs: [], popularBlogs: [] });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (ipAddress) fetchBlogs();
  }, [ipAddress]);

  useEffect(() => {
    isSearchCancelledRef.current = false;
  }, [ipAddress]);

  // ---------- Helpers ----------
  const formatDate = (dateString) => format(new Date(dateString), "d MMMM yyyy");

  const addViewsCount = async (uuid) => {
    try {
      await postProxy(`blog/views`, { blogUUID: uuid });
    } catch (error) {
      console.error("Error adding view count:", error);
    }
  };

  const handleBlogClick = (blog) => {
    addViewsCount(blog.blog_uuid);
    navigate(`/blogs/${blog.slug}`);
  };

  const handleLikeClick = async (blog) => {
    try {
      const alreadyLiked = blog.likes && blog.likes.length > 0;
      await postProxy(`blog/like`, { blogUUID: blog.blog_uuid, ip: ipAddress });

      setBlogData((prev) => {
        const updated = { ...prev };
        ["recentBlogs", "mostViewedBlogs", "popularBlogs"].forEach((key) => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].map((b) => {
              if (b.blog_uuid === blog.blog_uuid) {
                return alreadyLiked
                  ? { ...b, likesAdded: Math.max(0, b.likesAdded - 1), likes: [] }
                  : { ...b, likesAdded: b.likesAdded + 1, likes: [{}] };
              }
              return b;
            });
          }
        });
        return updated;
      });
    } catch (err) {
      console.log("Error toggling like:", err);
    }
  };

  const handleViewClick = (viewtype) => navigate(`/blogs/viewall/${viewtype}`);

  const handleCategoryClick = (type) => {
    const formattedType = type.trim().replace(/\s+/g, "-").toLowerCase();
    navigate(`/blogs/category/${formattedType}`);
  };

  // ---------- Search ----------
  const performSearch = useCallback(
    async (searchValue) => {
      if (isSearchCancelledRef.current || !searchValue.trim()) {
        setIsLoading(false);
        return;
      }

      latestSearchRef.current = searchValue;

      try {
        const result = await getProxy(`blog/search/${searchValue}/${ipAddress}`);

        if (isSearchCancelledRef.current || latestSearchRef.current !== searchValue) {
          return;
        }

        setBlogData(result.data || []);
        setSelectedTags([]);
      } catch (err) {
        console.error("Error searching blogs:", err);
        if (!isSearchCancelledRef.current) {
          setBlogData([]);
        }
      } finally {
        if (!isSearchCancelledRef.current) {
          setIsLoading(false);
        }
      }
    },
    [getProxy, ipAddress],
  );

  const [debouncedSearch, cancelSearch] = useDebounce(performSearch, 500);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchInput(value);
    setSelectedTags([]);

    isSearchCancelledRef.current = false;

    if (!value.trim()) {
      cancelSearch();
      isSearchCancelledRef.current = true;
      handleSearchClose();
      return;
    }

    setIsSearched(true);
    setIsLoading(true);

    debouncedSearch(value);
  };

  const handleSearchClose = () => {
    cancelSearch();
    isSearchCancelledRef.current = true;

    setSearchInput("");
    setIsSearched(false);
    setSelectedTags([]);
    setIsLoading(false);
    setTimeout(() => {
      isSearchCancelledRef.current = false;
      fetchBlogs();
    }, 0);
  };

  // ---------- Tags ----------
  const fetchBlogsByTags = async (selectedTagsByProps = selectedTags) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}blog/blogs-by-tags`, {
        params: { tags: selectedTagsByProps.join(","), ip: ipAddress },
      });
      setBlogData(response.data.data || {});
      setIsSearched(true);
    } catch (error) {
      console.error("Error fetching blogs by tags:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagChange = (tag) => {
    setSearchInput("");
    setSelectedTags((prevTags) => {
      const updatedTags = prevTags.includes(tag)
        ? prevTags.filter((t) => t !== tag)
        : [...prevTags, tag];

      if (updatedTags.length) {
        fetchBlogsByTags(updatedTags);
      } else {
        fetchBlogs();
        setIsSearched(false);
      }
      return updatedTags;
    });
  };

  // ---------- Section Renderer ----------
  function renderBlogSection(title, blogs, viewType) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mt-5">
          <h1 className="ft-wt-600 mb-0">{title}</h1>
          <Button
            className="p-0 text-primary ft-16"
            variant="link"
            onClick={() => handleViewClick(viewType)}
          >
            View All
          </Button>
        </div>
        <Row className="blog-cards">
          {blogs?.length ? (
            blogs.map((blog) => (
              <Col xs="12" lg="4" key={blog.blog_uuid} className="blog-card cursor-pointer">
                <img
                  src={blog.template_image}
                  alt={blog.title}
                  className="blog-thumbnail"
                  onClick={() => handleBlogClick(blog)}
                />
                <div className="blog-card-info">
                  <div className="blog-meta">
                    <span
                      className="author cursor-pointer"
                      onClick={() => handleCategoryClick(blog.service_category)}
                    >
                      {blog.service_category}
                    </span>
                    <span className="date">{formatDate(blog.created_at)}</span>
                  </div>
                  <h3 className="blog-card-title" onClick={() => handleBlogClick(blog)}>
                    {blog.title}
                  </h3>
                  <p className="blog-content" onClick={() => handleBlogClick(blog)}>
                    {blog.template_content}
                  </p>
                  <div className="blog-actions justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={blog?.likes?.length ? likeFilled : like}
                        alt="thumbs-up"
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeClick(blog);
                        }}
                      />
                      <img
                        src={share}
                        alt="share"
                        style={{ width: "16px" }}
                        className="cursor-pointer"
                        onClick={() => handleShareClick(blog)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">{blog.likesAdded} likes</h6>
                      <h6 className="mb-0">{blog.views} views</h6>
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <SkeletonScreen count={3} />
          )}
        </Row>
      </>
    );
  }

  // ---------- Render ----------
  return (
    <section className="blog-container">
      <Container fluid className="pb-5 pt-3">
        {/* 🔍 Search */}
        <h1 className="text-primary ft-wt-600 mb-3 mt-0">Blogs</h1>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search for articles, blogs, etc."
            onChange={handleInputChange}
            value={searchInput}
          />
          <img
            src={isSearched && searchInput ? closeIcon : search}
            alt="search"
            onClick={handleSearchClose}
            className={`${isSearched ? "cursor-pointer" : ""}`}
          />
        </div>

        {/* 🏷 Tags */}
        <div className="tags-container">
          {tags.map((tag, index) => (
            <div
              key={index}
              className={`tag ${selectedTags.includes(tag) ? "active" : ""}`}
              onClick={() => handleTagChange(tag)}
            >
              {tag}
              {selectedTags.includes(tag) && (
                <img
                  src="https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/circle-close.svg"
                  alt="circle-close"
                />
              )}
            </div>
          ))}
        </div>

        {/* 📑 Blog Sections */}
        {isLoading ? (
          <SkeletonScreen />
        ) : isSearched ? (
          <SearchResults
            keyword={searchInput}
            blogs={Array.isArray(blogData) ? blogData : []}
            setBlogData={setBlogData}
            selectedTags={selectedTags}
            fetchBlogsByTags={fetchBlogsByTags}
            isLoading={isLoading}
          />
        ) : (
          <>
            {renderBlogSection("What's New", blogData?.recentBlogs, "recent")}
            {renderBlogSection("Most Viewed", blogData?.mostViewedBlogs, "most-viewed")}
            {renderBlogSection("Popular Posts", blogData?.popularBlogs, "popular")}
          </>
        )}
      </Container>

      {/* 📤 Share Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="ft-wt-700">Share This Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3 text-center">
              <Form.Label className="copy-link-label">Share to</Form.Label>
              <div className="d-flex gap-2 justify-content-center">
                <FacebookShareButton url={shareURL}>
                  <FacebookIcon size={48} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareURL}>
                  <TwitterIcon size={48} round />
                </TwitterShareButton>
                <LinkedinShareButton url={shareURL}>
                  <LinkedinIcon size={48} round />
                </LinkedinShareButton>
                <EmailShareButton url={shareURL}>
                  <EmailIcon size={48} round />
                </EmailShareButton>
              </div>
            </Form.Group>
            <Form.Group className="mb-3 position-relative">
              <Form.Label className="copy-link-label">Or copy the link</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control type="text" value={shareURL} readOnly />
                <Button className="ms-2 p-0 copy-btn" variant="link" onClick={handleCopy}>
                  <img
                    src={isCopied ? copySuccess : copyLink}
                    alt={isCopied ? "copied" : "copy"}
                    className="cursor-pointer"
                  />
                </Button>
              </div>
              {isCopied && <div className="link-success-msg">Link copied successfully!</div>}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="ft-wt-700 text-primary text-decoration-none"
            onClick={handleCloseModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🎥 Testimonials + Contact + Partners */}
      <TestimonialCarousel />
      <Container>
        <Row>
          <Col xs="12" lg="6">
            <TestimonialVideoPlayer />
          </Col>
          <Col xs="12" lg="6">
            <SentryTestimonialVideoPlayer />
          </Col>
        </Row>
      </Container>
      <ContactForm />
      <ProductPartners />
    </section>
  );
};

export default BlogComponent;
